import { useEffect, useRef } from 'react';

import { useLocation } from 'react-router';

import { useAtomValue } from 'jotai';

import { AmplitudeProductEvent } from '@cfa/pathway-fe-shared';

import { useAmplitudeTrack } from '@/amplitude/useAmplitude';
import { documentAtom } from '@/atoms/document';

const MIN_SELECTION_LENGTH = 10;
const SELECTION_DEBOUNCE_MS = 500;

/**
 * App-wide text interaction tracking. Fires Amplitude events for:
 * - `text_copy` on the `copy` event
 * - `text_paste` on the `paste` event
 * - `text_selection` on selection settle (debounced, min-length, deduped)
 *
 * Mount once in Layout.tsx.
 */
export const useTextInteractionTracking = () => {
  const track = useAmplitudeTrack();
  const { pathname } = useLocation();
  const { document: currentDoc } = useAtomValue(documentAtom);

  const lastSelectionRef = useRef('');
  const selectionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    lastSelectionRef.current = '';
  }, [pathname, currentDoc?.id]);

  useEffect(() => {
    const resourceId = currentDoc?.id;

    const handleCopy = () => {
      const selection = window.getSelection()?.toString() ?? '';
      track(AmplitudeProductEvent.TEXT_COPY, {
        char_count: selection.length,
        route: pathname,
        resource_id: resourceId,
      });
    };

    const handlePaste = (e: ClipboardEvent) => {
      const pastedText = e.clipboardData?.getData('text/plain') ?? '';
      track(AmplitudeProductEvent.TEXT_PASTE, {
        char_count: pastedText.length,
        route: pathname,
      });
    };

    const handleSelectionChange = () => {
      if (selectionTimerRef.current) {
        clearTimeout(selectionTimerRef.current);
      }

      selectionTimerRef.current = setTimeout(() => {
        const text = window.getSelection()?.toString() ?? '';

        if (
          text.length >= MIN_SELECTION_LENGTH &&
          text !== lastSelectionRef.current
        ) {
          lastSelectionRef.current = text;
          track(AmplitudeProductEvent.TEXT_SELECTION, {
            char_count: text.length,
            route: pathname,
            resource_id: resourceId,
          });
        }
      }, SELECTION_DEBOUNCE_MS);
    };

    document.addEventListener('copy', handleCopy);
    document.addEventListener('paste', handlePaste);
    document.addEventListener('selectionchange', handleSelectionChange);

    return () => {
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('paste', handlePaste);
      document.removeEventListener('selectionchange', handleSelectionChange);
      if (selectionTimerRef.current) {
        clearTimeout(selectionTimerRef.current);
      }
    };
  }, [track, pathname, currentDoc?.id]);
};
